import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import GRNLink from "../components/GRNLink";
import { ChevronRight, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePager } from "../components/Pagination";
import { fmtQty } from "../lib/format";

export default function Stock() {
  const [summary, setSummary] = useState([]);
  const [fifo, setFifo] = useState([]);
  const [tab, setTab] = useState("summary");
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [openKey, setOpenKey] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/inventory/stock-summary"), api.get("/inventory/fifo-by-grn")]).then(([s, f]) => {
      setSummary(s.data); setFifo(f.data);
    });
  }, []);

  // All distinct categories present in summary OR fifo. "All" is always first.
  const categories = useMemo(() => {
    const set = new Set();
    summary.forEach((s) => set.add(s.category || "Uncategorised"));
    fifo.forEach((l) => set.add(l.category || "Uncategorised"));
    const list = [...set].sort();
    // Keep a stable order: Raw Material, Consumable, Asset first if present
    const preferred = ["Raw Material", "Consumable", "Asset"];
    const ordered = [...preferred.filter((p) => list.includes(p)),
      ...list.filter((c) => !preferred.includes(c))];
    return ["All", ...ordered];
  }, [summary, fifo]);

  const filteredSummary = summary.filter((s) => {
    if (category !== "All" && (s.category || "Uncategorised") !== category) return false;
    if (!search) return true;
    const k = search.toLowerCase();
    return s.code.toLowerCase().includes(k) || s.name.toLowerCase().includes(k);
  });
  const filteredFifo = fifo.filter((l) => {
    if (category !== "All" && (l.category || "Uncategorised") !== category) return false;
    if (!search) return true;
    const k = search.toLowerCase();
    return l.material_code.toLowerCase().includes(k) || l.material_name.toLowerCase().includes(k) || l.grn_number.toLowerCase().includes(k) || l.lot_no.toLowerCase().includes(k);
  });

  // Wave-K + Feb-2026 fix: paginate; "pb-24" keeps the controls clear of the
  // bottom-right Emergent watermark badge so page numbers are always tappable.
  const { visibleRows: pagedSummary, controls: summaryControls } = usePager(filteredSummary, 10);
  const { visibleRows: pagedFifo, controls: fifoControls } = usePager(filteredFifo, 10);

  return (
    <div data-testid="stock-page">
      <PageHeader title="Stock & FIFO Ledger" subtitle="Live inventory — oldest GRN first"
        helpText="Click a part code in Stock Summary to view its inward/outward history with running balance. FIFO Ledger shows GRN-level rollups — click a row to expand box-level detail. Use category tabs at the top to filter by inventory type."
      />
      <div className="p-8 pb-24">
        <div className="flex items-center gap-4 mb-3">
          <div className="flex bg-white border border-[#EEEEEE] rounded-sm overflow-hidden">
            <button onClick={() => setTab("summary")} data-testid="tab-summary" className={`px-4 py-2 text-xs uppercase tracking-widest font-bold ${tab === "summary" ? "bg-[#1565C0] text-white" : "text-[#0D2B5E]"}`}>Stock Summary</button>
            <button onClick={() => setTab("ledger")} data-testid="tab-ledger" className={`px-4 py-2 text-xs uppercase tracking-widest font-bold ${tab === "ledger" ? "bg-[#1565C0] text-white" : "text-[#0D2B5E]"}`}>FIFO Ledger</button>
          </div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code, name, GRN, Lot…" data-testid="stock-search" className="flex-1 max-w-md border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm font-mono"/>
        </div>

        {/* Category tab strip — Raw Material / Consumable / Asset / All */}
        <div className="flex flex-wrap gap-1 mb-4 border-b border-[#EEEEEE]">
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid={`stock-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-3 py-2 text-[11px] uppercase tracking-widest font-bold border-b-2 -mb-px ${category === c ? "text-[#1565C0] border-[#1565C0]" : "text-[#0D2B5E]/60 border-transparent hover:text-[#1565C0]"}`}>
              {c}
              <span className="ml-2 text-[#0D2B5E]/40 normal-case tracking-normal">
                ({tab === "summary"
                  ? summary.filter((s) => c === "All" || (s.category || "Uncategorised") === c).length
                  : fifo.filter((l) => c === "All" || (l.category || "Uncategorised") === c).length})
              </span>
            </button>
          ))}
        </div>

        {tab === "summary" && (
          <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2.5">Code</th>
                  <th className="text-left px-4 py-2.5">Material</th>
                  <th className="text-left px-4 py-2.5">Category</th>
                  <th className="text-left px-4 py-2.5">UOM</th>
                  <th className="text-right px-4 py-2.5">Current Qty</th>
                  <th className="text-right px-4 py-2.5">Min</th>
                  <th className="text-right px-4 py-2.5">Max</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {pagedSummary.map((s, i) => (
                  <tr key={s.material_id} className={`${i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"} hover:bg-[#1565C0]/5 cursor-pointer`}
                    onClick={() => nav(`/materials/${s.material_id}/history`)} data-testid={`stock-row-${s.code}`}>
                    <td className="px-4 py-2.5 font-mono text-[#1565C0] underline">{s.code}</td>
                    <td className="px-4 py-2.5">{s.name}</td>
                    <td className="px-4 py-2.5 text-xs">{s.category || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{s.uom}</td>
                    <td className="px-4 py-2.5 font-mono text-right">{fmtQty(s.qty)}</td>
                    <td className="px-4 py-2.5 font-mono text-right text-[#0D2B5E]/60">{fmtQty(s.min_stock)}</td>
                    <td className="px-4 py-2.5 font-mono text-right text-[#0D2B5E]/60">{fmtQty(s.max_stock)}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={s.status}/></td>
                  </tr>
                ))}
                {filteredSummary.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-sm text-[#0D2B5E]/50">No matches.</td></tr>}
              </tbody>
            </table>
            {summaryControls}
          </div>
        )}

        {tab === "ledger" && (
          <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                <tr>
                  <th className="text-left px-4 py-2.5 w-8"></th>
                  <th className="text-left px-4 py-2.5">Date In</th>
                  <th className="text-left px-4 py-2.5">GRN</th>
                  <th className="text-left px-4 py-2.5">Lot</th>
                  <th className="text-left px-4 py-2.5">Material</th>
                  <th className="text-left px-4 py-2.5">Category</th>
                  <th className="text-right px-4 py-2.5">Qty</th>
                  <th className="text-right px-4 py-2.5">Boxes</th>
                  <th className="text-left px-4 py-2.5">Deviation</th>
                </tr>
              </thead>
              <tbody>
                {pagedFifo.map((l, i) => {
                  const key = `${l.grn_number}-${l.lot_no}`;
                  const isOpen = openKey === key;
                  return (
                    <React.Fragment key={key}>
                      <tr className={`${i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"} hover:bg-[#1565C0]/5 cursor-pointer`}
                        onClick={() => setOpenKey(isOpen ? null : key)} data-testid={`fifo-row-${l.grn_number}`}>
                        <td className="px-4 py-2.5 text-[#1565C0]">{isOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</td>
                        <td className="px-4 py-2.5 text-xs font-mono">{l.date_in?.slice(0, 10)}</td>
                        <td className="px-4 py-2.5 font-mono text-[#1565C0] font-bold"><GRNLink number={l.grn_number}/></td>
                        <td className="px-4 py-2.5 font-mono">{l.lot_no}</td>
                        <td className="px-4 py-2.5">{l.material_code} — {l.material_name}</td>
                        <td className="px-4 py-2.5 text-xs">{l.category || "—"}</td>
                        <td className="px-4 py-2.5 font-mono text-right font-bold">{fmtQty(l.qty)}</td>
                        <td className="px-4 py-2.5 font-mono text-right">{l.boxes}</td>
                        <td className="px-4 py-2.5 text-xs">{l.deviation_flag || "—"}</td>
                      </tr>
                      {isOpen && (
                        <tr><td colSpan={9} className="bg-[#0D2B5E]/5 px-12 py-3" data-testid={`fifo-detail-${l.grn_number}`}>
                          <div className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60 mb-2">Box Detail ({l.boxes_detail.length} boxes)</div>
                          <div className="grid grid-cols-6 gap-2">
                            {l.boxes_detail.map((b) => (
                              <div key={b.box_no} className="bg-white border border-[#EEEEEE] px-2 py-1 rounded-sm font-mono text-xs flex justify-between">
                                <span className="text-[#0D2B5E]/60">Box {b.box_no}</span>
                                <span className="font-bold text-[#1565C0]">{fmtQty(b.qty_remaining)}</span>
                              </div>
                            ))}
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {filteredFifo.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-sm text-[#0D2B5E]/50">Empty ledger.</td></tr>}
              </tbody>
            </table>
            {fifoControls}
          </div>
        )}
      </div>
    </div>
  );
}
